import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classes from './PostCard.module.scss';
import classNames from 'classnames';
import { CheckboxWithAction } from '../../../../components/actions/CheckboxWithAction';
import { Icon } from '../../../../components/images/Icon/Icon';
import { ReactComponent as LinkPreviewNoImage } from '../../../../assets/images/linkPreviewNoImage.svg';
import { IconsEnum } from '../../../../constants/icons';
import {
  ThirdPartyPost,
  ThirdPartyPostAttachmentType,
  ThirdPartyPostMedia,
  ThirdPartyPostMediaType,
  TimeFormatSetting,
  TimezoneSetting
} from '../../../../models/graphql';
import { formatDistanceStrict } from 'date-fns';
import { useInView } from 'react-intersection-observer';
import { FindContentValueType, ProfileFindContent } from '../FindContentProvider';
import { ZoomModal } from '../ZoomModal';
import { Galleria } from 'primereact/galleria';
import { convertLinksToAnchorTags } from '../../../../helpers/textHelpers';
import { FindContentShareButton } from '../FindContentShareButton';
import { RedExclamationTooltip } from '../../../../components/actions/RedExclamationTooltip';
import { MaxSelectedPostsToImport } from '../../../../constants/find-content';
import { showToast } from '../../../../helpers/showToast';
import pluralize from 'pluralize';
import { Tooltip } from 'components/actions/Tooltip';
import { useSelectedOrganization } from 'hooks/useSelectedOrganization';
import { convertTimezoneEnumToName, convertUtcToTimezonedDate } from 'helpers/timeFormatter';
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz';
import { TimezoneNames } from 'constants/timezones';
import { useSettings } from 'hooks/useSettings';
import { WeekdayDayOrdinalMonthYearFullFormat } from 'constants/time-formats';
import uuidGen from '../../../../helpers/uuidGen';
import { DateStyle, FormatString } from 'hooks/useDateString';
import {
  ThirdPartyLinkPreviewContextEnum,
  ThirdPartyLinkPreviewViewProps
} from '../ThirdPartyLinkPreview/ThirdPartyLinkPreview.view';
import { ThirdPartyLinkPreview } from '../ThirdPartyLinkPreview';
import { getImageDimensions, isMediaIsAnimatedGif, MediaDimensions } from '../../../../helpers/media';
import { Loader } from '../../../../components/containers/Loader';
import { SocialNetworksEnum } from '../../../../constants/social-networks';
import { isUndefined } from 'lodash';
import { Debugger } from '../../../../components/containers/Debugger';
import { useDidMount } from '../../../../hooks/useDidMount';
import { ProfileMetrics } from 'constants/profileMetrics';
import { PostQueueStatsSet } from '../PostQueueStatsSet';
import { ElementOverlay } from '../../../../components/containers/ElementOverlay';
import {
  ElementOverlayCursor,
  ElementOverlayPositions,
  ElementOverlaySize
} from '../../../../components/containers/ElementOverlay/ElementOverlay.view';

export enum FindContentShareType {
  'ALL',
  'LINK',
  'IMAGE'
}

export type PostCardViewProps = {
  post: ThirdPartyPost;
  selectedPosts: FindContentValueType['selectedPosts'];
  setSelectedPosts: FindContentValueType['setSelectedPosts'];
  sourceType: FindContentValueType['sourceType'];
  availableMetrics: ProfileMetrics[];
  selectedProfile: ProfileFindContent | undefined;
};

export const hasLinkAttachmentType = (post: ThirdPartyPost): boolean => {
  return post.attachmentTypes.some(attachmentType => attachmentType === ThirdPartyPostAttachmentType.LINK);
};

const NoImagePlaceholder = (): JSX.Element => (
  <div className={classes.NoImageContainer}>
    <div className={classes.NoImageIconWrapper}>
      <LinkPreviewNoImage />
    </div>
  </div>
);

export const PostCardView = (props: PostCardViewProps): JSX.Element => {
  const { post, selectedPosts, setSelectedPosts, sourceType, availableMetrics, selectedProfile } = props;
  const { selectedWorkspace } = useSelectedOrganization();
  const { selectedOrgSettings } = useSettings();
  const [loadingGif, setLoadingGif] = useState<boolean>(true);
  const isAnimatedGifRef = useRef<boolean | undefined>(undefined);
  const [photoDimensions, setPhotoDimensions] = useState<MediaDimensions>({ width: 0, height: 0 });
  const { timezone } = selectedWorkspace || {};

  const timezoneName = convertTimezoneEnumToName(timezone ?? TimezoneSetting.UTC) ?? TimezoneNames.UTC;
  const postCreatedTimeWithWorkspaceTimezone = Boolean(post.createdAt)
    ? convertUtcToTimezonedDate(post.createdAt, timezoneName)
    : undefined;

  const [showZoom, setShowZoom] = useState<boolean>(false);

  const isChecked = !!Object.values(selectedPosts).find(p => p?.id === post.externalId);
  const isLinkPresent = useMemo(() => hasLinkAttachmentType(post), [post]);

  const hasMixedMedia =
    post.attachmentTypes.includes(ThirdPartyPostAttachmentType.VIDEO) &&
    post.attachmentTypes.includes(ThirdPartyPostAttachmentType.PHOTO);

  const stickyPositions = [
    ...(isChecked ? [ElementOverlayPositions.TopLeft] : []),
    ...(isLinkPresent ? [ElementOverlayPositions.BottomRight] : []),
    ...(hasMixedMedia ? [ElementOverlayPositions.BottomLeft] : [])
  ];

  const { ref, inView } = useInView({
    threshold: 0
  });

  const networksSupportingGifs = [SocialNetworksEnum.linkedin, SocialNetworksEnum.twitter, 'RSS'];
  const isGifSupportedOnPlatform = post.externalProfile.platform
    ? networksSupportingGifs.includes(post.externalProfile.platform)
    : false;

  const checkboxHandler = useCallback(
    (id: string, e: React.MouseEvent<HTMLInputElement>) => {
      if (e.currentTarget.checked && !selectedPosts.find(p => p.id === id)) {
        setSelectedPosts(old => {
          if (old.length < MaxSelectedPostsToImport) {
            return [...old, ...[{ id: id }]];
          } else {
            showToast({
              severity: 'warn',
              detail: `Note: only ${pluralize(
                'post',
                MaxSelectedPostsToImport,
                true
              )} were selected as it's the maximum number that can be bulk imported in one go.`,
              summary: 'Selection Limit'
            });
            return old;
          }
        });
      } else if (!e.currentTarget.checked) {
        const posts = [...selectedPosts];
        const idx = posts.findIndex(p => p?.id === id);

        delete posts[idx];

        // Remove any empty/undefined values
        const filteredPosts = posts.filter(p => p);

        setSelectedPosts(filteredPosts);
      }
    },
    [selectedPosts, setSelectedPosts]
  );

  const dateAsString = useMemo(
    () =>
      postCreatedTimeWithWorkspaceTimezone
        ? formatDistanceStrict(
            new Date(postCreatedTimeWithWorkspaceTimezone),
            utcToZonedTime(new Date(), timezoneName),
            {
              addSuffix: true,
              roundingMethod: 'ceil'
            }
          )
        : '',
    [postCreatedTimeWithWorkspaceTimezone, timezoneName]
  );

  // Find the first video in the post.media array.
  const video = post.media.find(m => m.type === ThirdPartyPostMediaType.VIDEO);
  // Get all the photos.
  const photos = post.media.filter(m => m.type === ThirdPartyPostMediaType.PHOTO);
  // If we have video or photos, we will never use the Banner link preview type
  const isBannerLinkPreview = !video && !photos.length;

  useEffect(() => {
    getImageDimensions(post.linkImageUrl || '').then(dimensions => {
      setPhotoDimensions(dimensions);
    });
  }, [post.linkImageUrl]);

  // If neither video nor photos exist, then use the link image URL.
  const media = useMemo(
    () =>
      video ||
      (photos && photos.length > 0
        ? photos[0]
        : post.linkImageUrl && photoDimensions.width > 10 && photoDimensions.height > 10
        ? { url: post.linkImageUrl, type: ThirdPartyPostMediaType.PHOTO }
        : undefined),
    [photoDimensions, photos, post.linkImageUrl, video]
  );

  const mediaImageUrl = media?.type === 'VIDEO' ? media.thumbnailUrl : media?.url ?? undefined;

  // Checks image to see if it is a Gif
  useDidMount(() => {
    if (isGifSupportedOnPlatform && media?.type === 'PHOTO' && isUndefined(isAnimatedGifRef.current)) {
      setLoadingGif(true);

      isMediaIsAnimatedGif(String(media.url))
        .then(result => {
          isAnimatedGifRef.current = result;
        })
        .catch(() => {
          // it is possible to get an exception for issues like
          // CORS policy: No 'Access-Control-Allow-Origin' header is present on the requested resource
          // so do not bubble up the catch
        })
        .finally(() => {
          setLoadingGif(false);
        });
    } else {
      // Since we set loading to true by default, we need to make sure we flip it for non PHOTO types
      setLoadingGif(false);
    }
  });

  const galleriaMedia = useMemo(
    () => (video && hasMixedMedia ? [...[video], ...photos] : !video ? photos : undefined),
    [hasMixedMedia, photos, video]
  );
  const galleriaItemTemplate = useCallback((item: ThirdPartyPostMedia): JSX.Element => {
    return <img src={item.thumbnailUrl ?? item.url ?? ''} alt={''} className={classes.MediaElement} />;
  }, []);

  const mediaWrapperClasses = classNames(classes.MediaWrapper, {
    [classes.MediaWrapperTextOnly]: !mediaImageUrl && !post.linkName,
    [classes.MediaWrapperWithLinkPreviewBanner]: post.linkName
  });
  const postTextWrapperClasses = classNames(classes.PostTextWrapper, {
    [classes.PostTextNoBanner]: !post.linkName
  });
  const postDetailsWrapper = classNames('grid-x grid-x-padding align-justify', classes.PostDetailsWrapper);

  const postCardClasses = classNames('margin-bottom-small-16', classes.PostCard);
  const postLinkWrapperClasses = classNames(classes.PostLinkWrapper, 'cell auto');
  const postQueueStatBlockClasses = classNames('grid-x', classes.PostQueueStatBlock);

  // If we have video/photo - always use badge so do not pass in link details data
  const linkPreviewProps: ThirdPartyLinkPreviewViewProps = {
    context: ThirdPartyLinkPreviewContextEnum.CARD,
    ...(isBannerLinkPreview
      ? { linkDescription: post.linkDescription, linkImageUrl: post.linkImageUrl, linkName: post.linkName }
      : { linkDescription: null, linkImageUrl: null, linkName: null }),
    linkUrl: post.linkUrl
  };

  const cardOverlay = (
    <div className={mediaWrapperClasses}>
      <ElementOverlay
        size={ElementOverlaySize.MEDIUM}
        defaultAction={() => setShowZoom(true)}
        cursor={ElementOverlayCursor.magnify}
        topLeft={
          <div className={classes.OverlayActionable}>
            <CheckboxWithAction
              name={'postItemCheckBox'}
              size={'xlarge'}
              onClickHandler={e => checkboxHandler(post.externalId, e)}
              value={post.externalId}
              checked={isChecked}
            />
          </div>
        }
        topRight={
          <div className={classes.MediaElementTopRight}>
            {video && !hasMixedMedia ? (
              <Icon name={IconsEnum.video} size={'large'} />
            ) : isAnimatedGifRef.current ? (
              <Icon name={IconsEnum.gifBadge} size={'large'} />
            ) : galleriaMedia && galleriaMedia.length > 1 ? (
              <Icon name={IconsEnum.carousel} size={'large'} />
            ) : (
              <></>
            )}
          </div>
        }
        bottomLeft={
          hasMixedMedia ? (
            <RedExclamationTooltip
              id={post.externalId}
              title={'Videos will be shared as images.'}
              description={'This is a post with mixed content. Only images can be shared.'}
              size={'small'}
            />
          ) : (
            <></>
          )
        }
        bottomRight={
          <>
            {isLinkPresent && !linkPreviewProps.linkName && (
              <ThirdPartyLinkPreview hasTooltipBadge={true} {...linkPreviewProps} />
            )}
          </>
        }
        stickyPositions={stickyPositions}>
        {mediaImageUrl ? (
          galleriaMedia && galleriaMedia.length > 1 ? (
            <Galleria
              value={galleriaMedia}
              numVisible={1}
              item={galleriaItemTemplate}
              showIndicators={true}
              showItemNavigators={true}
              showThumbnails={false}
              circular={true}
            />
          ) : (
            <img src={mediaImageUrl} alt={''} className={classes.MediaElement} />
          )
        ) : (
          <>
            {/* If we have a banner link preview, we add a placeholder image */}
            {!mediaImageUrl && post.linkName ? (
              <NoImagePlaceholder />
            ) : (
              <div className={postTextWrapperClasses}>
                <div className={classes.PostText}>{convertLinksToAnchorTags(post.text)}</div>
              </div>
            )}
          </>
        )}
      </ElementOverlay>
    </div>
  );
  // [Used for tooltip]: Create a uniqueId so we do not have to rely on ExternalId since that is not always unique
  const uniqueId = uuidGen();

  return (
    <div className={postCardClasses} ref={ref}>
      {loadingGif ? (
        <Loader variation={'dark'} size={'medium'} type={'inline'} />
      ) : (
        inView &&
        !loadingGif && (
          <div className={'grid-y align-justify full-height'}>
            <Debugger tooltipContent={'External ID: ' + props.post.externalId} modalContent={props.post} />
            <div className={'cell auto'}>
              {cardOverlay}
              {!mediaImageUrl && linkPreviewProps.linkName ? (
                <>
                  <ThirdPartyLinkPreview {...linkPreviewProps} />
                  <div className={postTextWrapperClasses}>
                    <div className={classes.PostText}>{convertLinksToAnchorTags(post.text)}</div>
                  </div>
                </>
              ) : (
                mediaImageUrl && (
                  <>
                    {isLinkPresent && linkPreviewProps.linkName && <ThirdPartyLinkPreview {...linkPreviewProps} />}
                    <div className={postTextWrapperClasses}>
                      <div className={classes.PostText}>{convertLinksToAnchorTags(post.text)}</div>
                    </div>
                  </>
                )
              )}
            </div>
            <div className={'cell shrink'}>
              <div className={'margin-top-small-8 margin-bottom-small-8'}>
                <div className={postDetailsWrapper}>
                  <div className={postLinkWrapperClasses}>
                    {sourceType && (
                      <div
                        className={classes.PostLinkUrl}
                        onClick={() =>
                          window.open(String(post.externalProfile.profileUrl), '_blank', 'noopener,noreferrer')
                        }>
                        @{post.externalProfile.displayName}
                      </div>
                    )}
                  </div>
                  <div
                    id={`postDate-${uniqueId}`}
                    className={classes.PostDateWrapper}
                    onClick={() => window.open(post.postUrl, '_blank', 'noopener,noreferrer')}>
                    {dateAsString}
                  </div>
                  {postCreatedTimeWithWorkspaceTimezone && (
                    <Tooltip type={'regular'} target={`#postDate-${uniqueId}`} position={'bottom'} pointer={true}>
                      <>
                        {formatInTimeZone(
                          postCreatedTimeWithWorkspaceTimezone,
                          timezoneName,
                          `${WeekdayDayOrdinalMonthYearFullFormat}, ${
                            selectedOrgSettings?.timeFormat === TimeFormatSetting.HOUR_24
                              ? FormatString[DateStyle.TIME_ONLY].HOUR_24
                              : FormatString[DateStyle.TIME_ONLY].HOUR_12
                          }`
                        )}
                      </>
                    </Tooltip>
                  )}
                </div>
              </div>
              <div className={'margin-top-small-24 margin-bottom-small-24'}>
                <div className={postQueueStatBlockClasses}>
                  {sourceType && (
                    <>
                      <div className={'cell auto'}>
                        <PostQueueStatsSet {...{ availableMetrics, post, selectedProfile }} />
                      </div>
                      <div className={'cell shrink align-justify'}>{/* Profile EngageAs Selector */}</div>
                    </>
                  )}
                </div>
              </div>
              <div className={classes.ShareButtonWrapper}>
                <FindContentShareButton post={post} isLinkPresent={isLinkPresent} />
              </div>
            </div>
            {showZoom && (
              <ZoomModal
                post={post}
                hasAnimatedGif={isAnimatedGifRef.current}
                closeHandler={setShowZoom}
                availableMetrics={availableMetrics}
                selectedProfile={selectedProfile}
              />
            )}
          </div>
        )
      )}
    </div>
  );
};
